/**
 * NL op-stack reducer (M5.md AC-2, the key invariant). The persisted artifact
 * of an NL-authored map is an ordered `MapOp[]` (stored in `maps.nl_op_stack`);
 * the rendered style is a PURE FOLD of that stack onto a base `MapSpec`:
 *
 *     renderedSpec = reduceStack(baseSpec, opStack)
 *
 * So: three stacked STYLE prompts compose; Undo = re-reduce over
 * `ops.slice(0, -1)`; Reset = empty stack (back to the base). Re-render never
 * calls the LLM. Ops funnel through the SAME `MapSpec` the GUI writes, so NL
 * and click produce identical state.
 *
 * `highlight` / `clear_highlight` / `fit_bounds` describe transient VIEW
 * effects, not persisted style — the reducer leaves style untouched for
 * highlight/clear (the renderer applies those as an overlay from QUERY
 * results) and only flips the fit flag for `fit_bounds`.
 */

import type { MapOp } from "@/lib/contracts/nl";
import {
  MARKER_SHAPES,
  MARKER_SIZES,
  type ColorBy,
  type FilterClause,
  type FilterOp,
  type MapSpec,
  type MarkerShape,
  type MarkerSize,
  type SizeBy,
} from "@/lib/map/mapspec";

/** Deep-ish clone of the spec regions a fold touches (leaves M4 render pure). */
function cloneSpec(spec: MapSpec): MapSpec {
  return {
    ...spec,
    view: { ...spec.view },
    encoding: { ...spec.encoding },
    clustering: { ...spec.clustering },
    heat: { ...spec.heat },
    filters: spec.filters.map((f) => ({ ...f })),
    popup: { ...spec.popup, fields: [...spec.popup.fields] },
    legend: { ...spec.legend },
  };
}

const asString = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);

/** Apply one MapOp to a spec, returning a new spec (never mutates the input). */
export function applyOp(spec: MapSpec, op: MapOp): MapSpec {
  const next = cloneSpec(spec);
  const p = op.params ?? {};

  switch (op.type) {
    case "set_color": {
      const column = asString(p.column);
      if (!column) return spec;
      const mode = p.mode === "numeric" ? "numeric" : "categorical";
      const prev = next.encoding.colorBy;
      // colorOverride composes onto a prior colorBy for the same column.
      const overrides =
        prev && prev.column === column
          ? { ...(prev.overrides ?? {}), ...((p.overrides as Record<string, string>) ?? {}) }
          : (p.overrides as Record<string, string> | undefined);
      const colorBy: ColorBy = { column, mode, ...(overrides ? { overrides } : {}) };
      next.encoding.colorBy = colorBy;
      return next;
    }
    case "set_style": {
      // Size-by / mode live here (the PRD's sizeBy/setSize/setMode).
      if (p.sizeBy && typeof p.sizeBy === "object") {
        const sb = p.sizeBy as { column?: string; min?: number; max?: number };
        if (sb.column) next.encoding.sizeBy = { column: sb.column, min: sb.min, max: sb.max } as SizeBy;
      }
      // Marker shape/size (FAIL-476) — e.g. "use pins", "make them circles",
      // "bigger markers". Only the given field changes; the other is kept. An
      // absent marker reads as the grandfathered circle (matching render), so a
      // size-only change on an old map stays a circle rather than flipping to pin.
      if (p.marker && typeof p.marker === "object") {
        const m = p.marker as { shape?: unknown; size?: unknown };
        const cur = next.marker ?? { shape: "circle" as MarkerShape, size: "m" as MarkerSize };
        const shape = MARKER_SHAPES.includes(m.shape as MarkerShape) ? (m.shape as MarkerShape) : cur.shape;
        const size = MARKER_SIZES.includes(m.size as MarkerSize) ? (m.size as MarkerSize) : cur.size;
        next.marker = { shape, size };
      }
      const mode = asString(p.mode);
      if (mode === "cluster") {
        next.clustering = { ...next.clustering, enabled: true };
        next.heat = { ...next.heat, enabled: false };
      } else if (mode === "heat") {
        next.heat = { ...next.heat, enabled: true, weightColumn: asString(p.weightColumn) };
        next.clustering = { ...next.clustering, enabled: false };
      } else if (mode === "markers") {
        next.clustering = { ...next.clustering, enabled: false };
        next.heat = { ...next.heat, enabled: false };
      }
      return next;
    }
    case "cluster": {
      const enabled = typeof p.enabled === "boolean" ? p.enabled : true;
      next.clustering = { ...next.clustering, enabled };
      if (enabled) next.heat = { ...next.heat, enabled: false };
      return next;
    }
    case "label_by": {
      const column = asString(p.column);
      if (!column) return spec;
      next.popup = { ...next.popup, enabled: true, titleColumn: column };
      return next;
    }
    case "set_filter": {
      const column = asString(p.column);
      const clauseOp = asString(p.op) as FilterOp | undefined;
      if (!column || !clauseOp) return spec;
      const clause: FilterClause = { column, op: clauseOp, value: p.value as FilterClause["value"] };
      // Replace any existing clause on the same column+op (a re-filter), else append.
      const idx = next.filters.findIndex((f) => f.column === column && f.op === clauseOp);
      if (idx >= 0) next.filters[idx] = clause;
      else next.filters.push(clause);
      return next;
    }
    case "clear_filter": {
      const column = asString(p.column);
      next.filters = column ? next.filters.filter((f) => f.column !== column) : [];
      return next;
    }
    case "set_basemap": {
      const basemap = asString(p.basemap);
      if (basemap) next.basemap = basemap;
      return next;
    }
    case "fit_bounds": {
      next.view = { ...next.view, fit: true };
      return next;
    }
    // Transient overlay effects — no persisted-style change.
    case "highlight":
    case "clear_highlight":
      return spec;
    default:
      return spec;
  }
}

/**
 * Fold an ordered op stack onto a base spec. The rendered style is exactly this
 * for any stack; undo/reset are just shorter stacks. Pure and LLM-free.
 */
export function reduceStack(base: MapSpec, ops: MapOp[]): MapSpec {
  return ops.reduce((spec, op) => applyOp(spec, op), base);
}

/** Undo the last op: the spec re-reduced over all but the final op. */
export function undo(base: MapSpec, ops: MapOp[]): { spec: MapSpec; ops: MapOp[] } {
  const nextOps = ops.slice(0, -1);
  return { spec: reduceStack(base, nextOps), ops: nextOps };
}

/** Replay the next undone op: deterministic, local, and model-free. */
export function redo(
  base: MapSpec,
  ops: MapOp[],
  redoOps: MapOp[],
): { spec: MapSpec; ops: MapOp[]; redoOps: MapOp[] } {
  const [next, ...remaining] = redoOps;
  const nextOps = next ? [...ops, next] : ops;
  return { spec: reduceStack(base, nextOps), ops: nextOps, redoOps: remaining };
}

/** Reset: clear the stack, back to the base spec. */
export function reset(base: MapSpec): { spec: MapSpec; ops: MapOp[] } {
  return { spec: base, ops: [] };
}
